import React, { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../../config/axiosInstance';
import { toast } from 'react-toastify';
import { MessageSquare, LifeBuoy } from 'lucide-react';
import StatisticsCards from '../components/StatisticsCards';
import EditRequestModal from '../components/EditRequestModal';
import ViewRequestModal from '../components/ViewRequestModal';
import SearchAndFilters from '../components/SearchAndFilters';
import RequestsTable from '../components/RequestsTable';
import Pagination from '../components/Pagination';
import ResolvedInfoModal from '../components/ResolvedInfoModal';
import AdminChatDashboard from '../components/AdminChatDashboard';

const AdminSupport = () => {
  // Tab state - persist in localStorage
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('adminSupportTab') || 'requests';
  });

  const [requests, setRequests] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [modalRequest, setModalRequest] = useState(null);
  const [modalNote, setModalNote] = useState('');
  const [editModalRequest, setEditModalRequest] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNote, setEditNote] = useState('');
  const [showResolvedInfo, setShowResolvedInfo] = useState(false);

  const fetchRequests = async (pageToFetch = 1, showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const params = {
        page: pageToFetch,
        limit: 10,
      };
      if (statusFilter !== 'ALL') params.status = statusFilter;

      const res = await axiosInstance.get('/api/service-requests/admin/all', {
        params,
      });
      const data = res?.data?.data || {};

      const fetchedRequests = data.requests || [];
      setRequests(fetchedRequests);

      if (data.pagination) setPagination(data.pagination);
      if (data.statistics) setStatistics(data.statistics);
    } catch (err) {
      setError('Failed to load support requests');
      toast.error('Failed to load support requests');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Fetch unread messages count
  const fetchUnreadCount = async () => {
    try {
      const response = await axiosInstance.get('/api/chat/rooms/all');
      if (response.data.success) {
        const rooms = response.data.data.chatRooms || [];
        setUnreadCount(
          rooms.reduce((total, room) => total + (room.unreadCount || 0), 0),
        );
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Save active tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('adminSupportTab', activeTab);
  }, [activeTab]);

  // Initial data fetch on mount
  useEffect(() => {
    fetchRequests(page);
    fetchUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch requests when page or filter changes and on requests tab
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchRequests(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, activeTab]);

  // Refresh the active support data without replacing the current page or tab.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (activeTab === 'requests') fetchRequests(page, false);
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, statusFilter]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const statusOptions = useMemo(
    () => ['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'CANCELLED'],
    []
  );

  // Client-side search filtering
  const filteredRequests = useMemo(() => {
    if (!search || !search.trim()) {
      return requests;
    }

    const searchLower = search.trim().toLowerCase();
    return requests.filter((r) => {
      return (
        r.id?.toLowerCase().includes(searchLower) ||
        r.userId?.toLowerCase().includes(searchLower) ||
        r.user?.email?.toLowerCase().includes(searchLower) ||
        r.subject?.toLowerCase().includes(searchLower) ||
        r.message?.toLowerCase().includes(searchLower) ||
        r.user?.fullName?.toLowerCase().includes(searchLower) ||
        r.category?.toLowerCase().includes(searchLower) ||
        r.priority?.toLowerCase().includes(searchLower) ||
        r.status?.toLowerCase().includes(searchLower)
      );
    });
  }, [requests, search]);

  const handleViewRequest = (request) => {
    setModalRequest(request);
    setModalNote('');
  };

  const handleEditRequest = (request) => {
    if (request.status === 'RESOLVED') {
      setShowResolvedInfo(true);
      return;
    }
    setEditModalRequest(request);
    setEditStatus(request.status || '');
    setEditNote('');
  };

  const handleCloseEditModal = () => {
    setEditModalRequest(null);
    setEditStatus('');
    setEditNote('');
  };

  const handleCloseViewModal = () => {
    setModalRequest(null);
  };

  const handleEditSuccess = (optimisticStatus) => {
    if (optimisticStatus) {
      setRequests((prev) =>
        prev.map((it) =>
          it.id === editModalRequest.id
            ? { ...it, status: optimisticStatus }
            : it
        )
      );
    }
    setEditModalRequest(null);
    setEditStatus('');
    setEditNote('');
    fetchRequests(page);
  };

  const handleViewSuccess = (optimisticStatus) => {
    if (optimisticStatus) {
      setRequests((prev) =>
        prev.map((it) =>
          it.id === modalRequest.id ? { ...it, status: optimisticStatus } : it
        )
      );
    }
    setModalRequest(null);
    fetchRequests(page);
  };

  return (
    <div className='container-fluid py-4'>
      {/* PAGE TITLE */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h3 className='fw-bold'>
          <LifeBuoy size={28} className='me-2' />
          Support Center
        </h3>
      </div>

      {/* TABS NAVIGATION */}
      <div className='card border-0 shadow-sm mb-4'>
        <div className='card-body p-0'>
          <ul className='nav nav-tabs border-0'>
            <li className='nav-item'>
              <button
                className={`nav-link px-4 py-3 d-flex align-items-center gap-2 ${
                  activeTab === 'requests' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('requests')}
                style={{
                  border: 'none',
                  borderBottom:
                    activeTab === 'requests'
                      ? '3px solid #667eea'
                      : '3px solid transparent',
                  backgroundColor:
                    activeTab === 'requests' ? '#f8f9fa' : 'transparent',
                  fontWeight: activeTab === 'requests' ? '600' : '500',
                  color: activeTab === 'requests' ? '#667eea' : '#6c757d',
                }}
              >
                <LifeBuoy size={20} />
                Support Requests
                {requests.length > 0 && (
                  <span className='badge bg-primary rounded-pill'>
                    {requests.length}
                  </span>
                )}
              </button>
            </li>
            <li className='nav-item'>
              <button
                className={`nav-link px-4 py-3 d-flex align-items-center gap-2 ${
                  activeTab === 'chat' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('chat')}
                style={{
                  border: 'none',
                  borderBottom:
                    activeTab === 'chat'
                      ? '3px solid #667eea'
                      : '3px solid transparent',
                  backgroundColor:
                    activeTab === 'chat' ? '#f8f9fa' : 'transparent',
                  fontWeight: activeTab === 'chat' ? '600' : '500',
                  color: activeTab === 'chat' ? '#667eea' : '#6c757d',
                }}
              >
                <MessageSquare size={20} />
                Live Chat Support
                {unreadCount > 0 && (
                  <span className='badge bg-success rounded-pill'>
                    {unreadCount} New
                  </span>
                )}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* TAB CONTENT */}
      {activeTab === 'requests' ? (
        <>
          {/* STAT CARDS */}
          <StatisticsCards statistics={statistics} />

          {/* MODALS */}
          <ResolvedInfoModal
            isOpen={showResolvedInfo}
            onClose={() => setShowResolvedInfo(false)}
          />

          <EditRequestModal
            editModalRequest={editModalRequest}
            editStatus={editStatus}
            setEditStatus={setEditStatus}
            editNote={editNote}
            setEditNote={setEditNote}
            onClose={handleCloseEditModal}
            onSuccess={handleEditSuccess}
          />

          <ViewRequestModal
            modalRequest={modalRequest}
            modalNote={modalNote}
            setModalNote={setModalNote}
            onClose={handleCloseViewModal}
            onSuccess={handleViewSuccess}
          />

          {/* SEARCH + FILTERS */}
          <SearchAndFilters
            search={search}
            setSearch={setSearch}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            statusOptions={statusOptions}
            setPage={setPage}
            onRefresh={() => fetchRequests(page)}
          />

          {/* REQUESTS TABLE */}
          <RequestsTable
            loading={loading}
            error={error}
            filteredRequests={filteredRequests}
            search={search}
            onViewRequest={handleViewRequest}
            onEditRequest={handleEditRequest}
          />

          {/* PAGINATION */}
          {pagination.totalPages > 1 && (
            <Pagination
              currentPage={pagination.currentPage || page}
              totalPages={pagination.totalPages || 1}
              onPageChange={(newPage) => {
                setPage(newPage);
                fetchRequests(newPage);
              }}
              totalItems={pagination.totalItems || requests.length}
              itemsPerPage={10}
            />
          )}
        </>
      ) : (
        <AdminChatDashboard onMessageRead={fetchUnreadCount} />
      )}
    </div>
  );
};

export default AdminSupport;
